import React, { useState, useRef, useEffect } from 'react'
import { func, string } from 'prop-types'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { TextField, List, ListItem } from '@fs/zion-ui'
import Debouncer from './debouncer'
import fetchPlaceOptions from './fetchPlaceOptions'

const borderedListCss = css`
  background: var(--gray05);
  border: 1px solid #aaaaaa;
  position: absolute;
  z-index: 1;
  height: 200px;
  overflow: auto;
  top: 70px;
  padding: 5px;
  width: 100%;
`

const PLACE_SEARCH_REQUEST_INTERVAL = 500
const debouncer = Debouncer(PLACE_SEARCH_REQUEST_INTERVAL)

export default function PlaceInput({ fieldName, idFieldName, fieldValue, idFieldValue, label, onOptionSelected }) {
  const [t] = useTranslation()
  const [inputText, setInputText] = useState(fieldValue || '')
  const [inputId, setInputId] = useState(idFieldValue || '')
  const [showList, setShowList] = useState(false)
  const [options, setOptions] = useState([])
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [selectedOption, setSelectedOption] = useState({ primary: fieldValue, placeId: idFieldValue })

  const useOutsideClickNotifier = (ref) => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setShowList(false)
      }
    }
    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    })
  }
  const clickRef = useRef(null)
  useOutsideClickNotifier(clickRef)

  useEffect(() => {
    if (fieldValue && idFieldValue) {
      setSelectedOption({ primary: fieldValue, placeId: idFieldValue })
      setInputText(fieldValue)
      setInputId(idFieldValue)
    } else {
      setSelectedOption(undefined)
      setInputText(fieldValue ?? '')
      setInputId(idFieldValue ?? '')
    }
  }, [fieldValue, idFieldValue])

  useEffect(() => {
    setOptions([])
    debouncer.debounce(inputText, setDebouncedSearchTerm)
  }, [inputText])

  useEffect(() => {
    const hasSearchTerm = !!debouncedSearchTerm && debouncedSearchTerm.trim() !== ''
    const hasPlaceId = !!selectedOption?.placeId
    const needsLookup = hasSearchTerm && !hasPlaceId

    if (needsLookup) {
      fetchPlaceOptions(debouncedSearchTerm).then((response) => {
        if (response) {
          setOptions(response)
          if (inputText !== selectedOption?.primary) {
            setShowList(true)
          }
        }
      })
    } else {
      setOptions([])
    }
  }, [debouncedSearchTerm, inputText, selectedOption])

  return (
    <>
      <TextField
        data-testid="place-input-search-for-a-place-text-field"
        autocomplete="off"
        label={label}
        name={fieldName}
        onChange={(e) => {
          const nextValue = e.target.value
          setInputText(nextValue)
          setInputId('')
          setSelectedOption(undefined)
          onOptionSelected({ primary: nextValue, placeId: '' })
        }}
        placeholder={t('atf.AncestorEditor.placeholders.place', 'Search for a place')}
        value={inputText}
      />
      <input data-testid="place-input-input" type="hidden" value={inputId} name={idFieldName} />

      {showList && options.length > 0 && (
        <div css={borderedListCss} ref={clickRef}>
          <List>
            {options.map((el) => (
              <ListItem
                data-testid="place-input-list-item"
                key={el?.key}
                primaryText={el?.primary}
                secondaryText={el?.secondary}
                onClick={() => {
                  const selected = options.find((opt) => opt?.placeId === el.key)
                  setSelectedOption(selected)
                  onOptionSelected(selected)
                  setInputText(selected?.primary ?? '')
                  setInputId(selected?.placeId ?? '')
                  setShowList(false)
                }}
              />
            ))}
          </List>
        </div>
      )}
    </>
  )
}

PlaceInput.propTypes = {
  fieldName: string,
  idFieldName: string,
  fieldValue: string,
  idFieldValue: string,
  label: string,
  onOptionSelected: func,
}
